/// `httpsCallable` for every Cloud Function call in the web app.
///
/// Same signature as Firebase's. The returned function waits for App
/// Check (started late, see `app.ts`) and its first token before it
/// calls, so a function with `enforceAppCheck: true` always gets one.
/// The token wait is the one the SDK would make anyway; the extra step
/// is only that the Functions SDK picks App Check up asynchronously
/// (`appCheckProvider.get().then(...)`), so a call made in the same
/// tick as `initializeAppCheck` would otherwise go out without it.

import { getToken } from 'firebase/app-check'
import {
  httpsCallable as firebaseHttpsCallable,
  type Functions,
  type HttpsCallableOptions,
  type HttpsCallableResult,
} from 'firebase/functions'
import { appCheckReady } from './app'

let tokenReady: Promise<void> | null = null

function appCheckTokenReady(): Promise<void> {
  // A failed token is not retried here: the SDK logs it and sends the
  // call without the header, as it did before App Check started late.
  tokenReady ??= appCheckReady.then((appCheck) => getToken(appCheck).then(() => undefined, () => undefined))
  return tokenReady
}

export function httpsCallable<RequestData = unknown, ResponseData = unknown>(
  functions: Functions,
  name: string,
  options?: HttpsCallableOptions,
): (data?: RequestData | null) => Promise<HttpsCallableResult<ResponseData>> {
  const call = firebaseHttpsCallable<RequestData, ResponseData>(functions, name, options)
  return async (data) => {
    await appCheckTokenReady()
    return call(data)
  }
}
